import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, isNil } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { Checkbox } from '@/components/forms/Checkbox'
import { Pill } from '@/components/pills/Pill'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import { getEvidenceCallId } from '@/utils/promptFeedback.helpers'
import {
  createPromptTest,
  deletePromptTest,
  updatePromptTest,
} from '@/reducers/prompts/prompts.actions'
import { setReportedCallsDismissed } from '@/reducers/promptFeedback/promptFeedback.actions'

import { AddTestCaseForm } from './AddTestCaseForm'
import { TestCaseRow } from './TestCaseRow'

const EVIDENCE_VIEW_PENDING = 'pending'
const EVIDENCE_VIEW_DISMISSED = 'dismissed'

const RESULT_FILTER_ALL = 'all'
const RESULT_FILTER_PASS = 'pass'
const RESULT_FILTER_FAIL = 'fail'

const matchesResultFilter = (result, resultFilter) => {
  if (resultFilter === RESULT_FILTER_ALL) {
    return true
  }
  if (isNil(result)) {
    return false
  }
  return resultFilter === RESULT_FILTER_PASS ? result.responses_match : !result.responses_match
}

/**
 * Component to display and run test cases (evidence-based, manual, and saved prompt tests)
 */
export const TestCaseList = ({
  allEvidence,
  dismissedEvidence = [],
  prompt,
  expectedScore,
  feedbackText,
  allTestResults,
  expandedTests,
  setExpandedTests,
  testLoading,
  runAllLoading,
  handleRunSingleTest,
  mustBePresent,
  manualTestCases = [],
  addManualTestCase,
  removeManualTestCase,
  handleRunManualTest,
  handleRunPromptTest,
  criteriaScid,
  taskId,
  organizationId,
  canEdit = true,
}) => {
  const dispatch = useDispatch()
  const { prompts } = useSelector((state) => state.prompts)
  const [savedTestIds, setSavedTestIds] = useState([])
  const [deletingIds, setDeletingIds] = useState([])
  const [dismissingCallIds, setDismissingCallIds] = useState([])
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedCallIds, setSelectedCallIds] = useState([])
  const [bulkSaving, setBulkSaving] = useState(false)
  const [evidenceView, setEvidenceView] = useState(EVIDENCE_VIEW_PENDING)
  const [resultFilter, setResultFilter] = useState(RESULT_FILTER_ALL)

  // Restoring the last dismissed call removes the toggle itself, so the view must fall
  // back with it — otherwise the user is stranded on an empty Dismissed view with no way
  // out.
  const dismissedCount = dismissedEvidence.length
  useEffect(() => {
    if (dismissedCount === 0) {
      setEvidenceView(EVIDENCE_VIEW_PENDING)
    }
  }, [dismissedCount])

  if (!prompt) {
    return null
  }

  const promptTests = prompt?.prompt_tests || []

  const existingPromptTestCallIds = promptTests.map((test) => test.inputs?.call_id).filter(Boolean)

  // Collect all call/message IDs to exclude when finding new test calls
  const existingCallIds = [
    ...allEvidence.map((evidence) => evidence.call_id || evidence.message_id).filter(Boolean),
    ...manualTestCases.map((testCase) => testCase.call_id || testCase.message_id).filter(Boolean),
    ...promptTests.map((test) => test.inputs?.call_id || test.inputs?.message_id).filter(Boolean),
  ]

  const handleSaveToPrompt = async (
    callId,
    expectedResult,
    callType = 'call',
    manualTestCaseId = null
  ) => {
    const promptTest = {
      prompt_uuid: prompt.prompt_uuid,
      expected_response: expectedResult,
      inputs: {
        call_id: callId,
        call_type: callType,
      },
    }

    await dispatch(createPromptTest(prompt, promptTest))
    setSavedTestIds((prev) => [...prev, callId])

    // If this was a manual test case, remove it (it now appears in saved section)
    if (manualTestCaseId && removeManualTestCase) {
      removeManualTestCase(manualTestCaseId)
    }
  }

  const handleDeletePromptTest = async (testId) => {
    if (deletingIds.includes(testId)) {
      return
    }
    setDeletingIds((prev) => [...prev, testId])
    try {
      await dispatch(deletePromptTest(prompts, prompt, testId))
    } finally {
      setDeletingIds((prev) => prev.filter((id) => id !== testId))
    }
  }

  const handleSetDismissed = async (callIds, dismissed) => {
    if (callIds.some((callId) => dismissingCallIds.includes(callId))) {
      return
    }
    setDismissingCallIds((previous) => [...previous, ...callIds])
    try {
      await dispatch(setReportedCallsDismissed({ taskId, callIds, organizationId, dismissed }))
      setSelectedCallIds((previous) => previous.filter((id) => !callIds.includes(id)))
    } finally {
      setDismissingCallIds((previous) => previous.filter((id) => !callIds.includes(id)))
    }
  }

  const exitSelectionMode = () => {
    setSelectionMode(false)
    setSelectedCallIds([])
  }

  const toggleSelected = (callId) => {
    setSelectedCallIds((previous) =>
      previous.includes(callId) ? previous.filter((id) => id !== callId) : [...previous, callId]
    )
  }

  const canDismiss = canEdit && !isNil(taskId)

  const hasAnyResults = !isEmpty(allTestResults)

  const visibleEvidence = allEvidence.filter((evidenceItem) =>
    matchesResultFilter(allTestResults[evidenceItem.id], resultFilter)
  )
  const visiblePromptTests = promptTests.filter((test) =>
    matchesResultFilter(allTestResults[`prompt-test-${test.id}`], resultFilter)
  )
  const visibleManualTestCases = manualTestCases.filter((testCase) =>
    matchesResultFilter(allTestResults[testCase.id], resultFilter)
  )

  const resultFilterItems = [
    { accessor: RESULT_FILTER_ALL, label: 'All' },
    { accessor: RESULT_FILTER_PASS, label: 'Pass' },
    { accessor: RESULT_FILTER_FAIL, label: 'Fail' },
  ].map((item) => ({
    ...item,
    active: resultFilter === item.accessor,
    onClick: () => setResultFilter(item.accessor),
    dataTestId: `test-result-filter-${item.accessor}`,
  }))

  const noVisibleTests =
    isEmpty(visibleEvidence) && isEmpty(visiblePromptTests) && isEmpty(visibleManualTestCases)

  const showingDismissed = evidenceView === EVIDENCE_VIEW_DISMISSED
  const evidenceViewItems = [
    { accessor: EVIDENCE_VIEW_PENDING, label: 'Pending', badge: allEvidence.length },
    { accessor: EVIDENCE_VIEW_DISMISSED, label: 'Dismissed', badge: dismissedEvidence.length },
  ].map((item) => ({
    ...item,
    active: evidenceView === item.accessor,
    onClick: () => setEvidenceView(item.accessor),
    dataTestId: `evidence-view-${item.accessor}`,
  }))

  const visibleCallIds = visibleEvidence.map((evidenceItem) => getEvidenceCallId(evidenceItem))
  const allVisibleSelected =
    !isEmpty(visibleCallIds) && visibleCallIds.every((callId) => selectedCallIds.includes(callId))
  const selectedEvidence = visibleEvidence.filter((evidenceItem) =>
    selectedCallIds.includes(getEvidenceCallId(evidenceItem))
  )

  const expectedFor = (evidenceItem) =>
    evidenceItem.expected_result ||
    (allEvidence[0]?.id === evidenceItem.id ? expectedScore : null) ||
    'yes'

  const bulkTesting = selectedEvidence.some((evidenceItem) => testLoading[evidenceItem.id])

  const handleTestSelected = () =>
    Promise.all(
      selectedEvidence.map((evidenceItem) =>
        handleRunSingleTest(evidenceItem, expectedFor(evidenceItem))
      )
    )

  const handleSaveSelected = async () => {
    setBulkSaving(true)
    try {
      // Sequential on purpose: each save rewrites the prompt's test list, so parallel
      // saves would race and drop each other's tests.
      for (const evidenceItem of selectedEvidence) {
        const callId = getEvidenceCallId(evidenceItem)
        if (!existingPromptTestCallIds.includes(callId) && !savedTestIds.includes(callId)) {
          // eslint-disable-next-line no-await-in-loop
          await handleSaveToPrompt(
            callId,
            expectedFor(evidenceItem),
            evidenceItem.call_type || 'call'
          )
        }
      }
    } finally {
      setBulkSaving(false)
    }
  }

  return (
    <div className="test-cases">
      {!isEmpty(dismissedEvidence) && (
        <div className="test-cases-filter">
          <SegmentedControl items={evidenceViewItems} />
        </div>
      )}

      {showingDismissed && (
        <div className="test-cases-section">
          <div className="test-cases-list">
            {dismissedEvidence.map((evidenceItem) => {
              const callId = getEvidenceCallId(evidenceItem)
              return (
                <TestCaseRow
                  key={evidenceItem.id}
                  id={evidenceItem.id}
                  callId={callId}
                  isMessage={evidenceItem.call_type === 'message'}
                  expectedResult={evidenceItem.expected_result}
                  userFeedback={evidenceItem.user_feedback || feedbackText}
                  reasoning={evidenceItem.ai_explanation}
                  isDismissed
                  isExpanded={expandedTests[evidenceItem.id] ?? false}
                  setExpandedTests={setExpandedTests}
                  onRestore={canDismiss ? () => handleSetDismissed([callId], false) : undefined}
                  isDismissing={dismissingCallIds.includes(callId)}
                />
              )
            })}
          </div>
        </div>
      )}

      {!showingDismissed && hasAnyResults && (
        <div className="test-cases-filter">
          <SegmentedControl items={resultFilterItems} />
        </div>
      )}

      {/* Section 1: Evidence-based test cases from user feedback */}
      {!showingDismissed && !isEmpty(visibleEvidence) && (
        <div className="test-cases-section">
          {canDismiss && (
            <div className="test-cases-bulk-bar">
              {selectionMode ? (
                <Checkbox
                  label="Select all"
                  checked={allVisibleSelected}
                  indeterminate={!allVisibleSelected && !isEmpty(selectedCallIds)}
                  onChange={() => setSelectedCallIds(allVisibleSelected ? [] : visibleCallIds)}
                  data-testid="select-all-reported-calls"
                />
              ) : (
                <span />
              )}
              <div className="flex-align-center small-gap">
                {!selectionMode && (
                  <Button
                    small
                    onClick={() => setSelectionMode(true)}
                    data-testid="enter-selection-mode"
                  >
                    Select
                  </Button>
                )}
                {selectionMode && !isEmpty(selectedCallIds) && (
                  <>
                    <span className="muted-text">{selectedCallIds.length} selected</span>
                    <Button
                      small
                      onClick={handleSaveSelected}
                      loading={bulkSaving}
                      disabled={bulkSaving}
                      tooltip="Save to prompt for ongoing regression testing"
                      tooltipProps={{ placement: 'top' }}
                      data-testid="save-selected-calls"
                    >
                      Save to prompt
                    </Button>
                    <Button
                      small
                      onClick={() => handleSetDismissed(selectedCallIds, true)}
                      loading={!isEmpty(dismissingCallIds)}
                      disabled={!isEmpty(dismissingCallIds)}
                      data-testid="dismiss-selected-calls"
                    >
                      Dismiss
                    </Button>
                    <Button
                      small
                      onClick={handleTestSelected}
                      loading={bulkTesting || runAllLoading}
                      disabled={bulkTesting || runAllLoading}
                      data-testid="test-selected-calls"
                    >
                      Test
                    </Button>
                  </>
                )}
                {selectionMode && (
                  <Button small onClick={exitSelectionMode} data-testid="exit-selection-mode">
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          )}
          <div className="test-cases-list">
            {visibleEvidence.map((evidenceItem) => {
              const callId = getEvidenceCallId(evidenceItem)
              const isMessage = evidenceItem.call_type === 'message'
              const evidenceExpected = expectedFor(evidenceItem)
              const result = allTestResults[evidenceItem.id]
              const isExpanded = expandedTests[evidenceItem.id] ?? true
              const isAlreadySaved =
                existingPromptTestCallIds.includes(callId) || savedTestIds.includes(callId)
              const promptOutdated =
                evidenceItem.prompt_id &&
                evidenceItem.current_prompt_id &&
                evidenceItem.prompt_id !== evidenceItem.current_prompt_id
              const oldPrompt = prompts.find(
                (promptVersion) => promptVersion.id === evidenceItem.prompt_id
              )
              const currentPrompt = prompts.find(
                (promptVersion) => promptVersion.id === evidenceItem.current_prompt_id
              )

              return (
                <TestCaseRow
                  key={evidenceItem.id}
                  id={evidenceItem.id}
                  callId={callId}
                  isMessage={isMessage}
                  expectedResult={evidenceExpected}
                  userFeedback={evidenceItem.user_feedback || feedbackText}
                  reasoning={evidenceItem.ai_explanation}
                  promptOutdated={promptOutdated}
                  oldPrompt={oldPrompt}
                  currentPrompt={currentPrompt}
                  result={result}
                  isExpanded={isExpanded}
                  setExpandedTests={setExpandedTests}
                  testLoading={testLoading}
                  runAllLoading={runAllLoading}
                  onRunTest={() => handleRunSingleTest(evidenceItem, evidenceExpected)}
                  mustBePresent={mustBePresent}
                  onSaveToPrompt={
                    canEdit
                      ? () =>
                          handleSaveToPrompt(
                            callId,
                            evidenceExpected,
                            evidenceItem.call_type || 'call'
                          )
                      : undefined
                  }
                  isAlreadySaved={isAlreadySaved}
                  onDismiss={canDismiss ? () => handleSetDismissed([callId], true) : undefined}
                  isDismissing={dismissingCallIds.includes(callId)}
                  isSelected={selectedCallIds.includes(callId)}
                  onToggleSelect={
                    canDismiss && selectionMode ? () => toggleSelected(callId) : undefined
                  }
                />
              )
            })}
          </div>
        </div>
      )}

      {/* Section 2: Saved prompt tests loaded from the prompt */}
      {!showingDismissed && !isEmpty(visiblePromptTests) && (
        <div className="test-cases-section">
          <div className="test-cases-list">
            {visiblePromptTests.map((test) => {
              const callId = test.inputs?.call_id || test.inputs?.message_id
              const isMessage = test.inputs?.call_type === 'message'
              const testKey = `prompt-test-${test.id}`
              const result = allTestResults[testKey]
              const isExpanded = expandedTests[testKey] ?? true

              // Filter out auto-generated descriptions from legacy saved tests
              const displayDescription =
                test.description && !test.description.startsWith('Auto-found')
                  ? test.description
                  : null

              return (
                <TestCaseRow
                  key={testKey}
                  id={testKey}
                  callId={callId}
                  isMessage={isMessage}
                  expectedResult={test.expected_response}
                  description={displayDescription}
                  result={result}
                  isExpanded={isExpanded}
                  setExpandedTests={setExpandedTests}
                  testLoading={testLoading}
                  runAllLoading={runAllLoading}
                  onRunTest={() => handleRunPromptTest(test)}
                  onRemove={canEdit ? () => handleDeletePromptTest(test.id) : undefined}
                  onEditTest={
                    canEdit
                      ? (updates) => dispatch(updatePromptTest(prompt.id, test.id, updates))
                      : undefined
                  }
                  mustBePresent={mustBePresent}
                  isSavedPromptTest
                  isDeleting={deletingIds.includes(test.id)}
                />
              )
            })}
          </div>
        </div>
      )}

      {/* Section 3: Manual test cases added during this session */}
      {!showingDismissed && !isEmpty(visibleManualTestCases) && (
        <div className="test-cases-section">
          <div className="test-cases-list">
            {visibleManualTestCases.map((testCase) => {
              const callId = testCase.call_id || testCase.message_id
              const isMessage = testCase.call_type === 'message'
              const result = allTestResults[testCase.id]
              const isExpanded = expandedTests[testCase.id] ?? true
              const isAlreadySaved =
                existingPromptTestCallIds.includes(callId) || savedTestIds.includes(callId)

              return (
                <TestCaseRow
                  key={testCase.id}
                  id={testCase.id}
                  callId={callId}
                  isMessage={isMessage}
                  expectedResult={testCase.expected_result}
                  description={testCase.description}
                  result={result}
                  isExpanded={isExpanded}
                  setExpandedTests={setExpandedTests}
                  testLoading={testLoading}
                  runAllLoading={runAllLoading}
                  onRunTest={() => handleRunManualTest(testCase)}
                  onRemove={() => removeManualTestCase(testCase.id)}
                  mustBePresent={mustBePresent}
                  isManual
                  onSaveToPrompt={
                    canEdit
                      ? () =>
                          handleSaveToPrompt(
                            callId,
                            testCase.expected_result,
                            testCase.call_type || 'call',
                            testCase.id // Pass the manual test case ID so it gets removed after saving
                          )
                      : undefined
                  }
                  isAlreadySaved={isAlreadySaved}
                />
              )
            })}
          </div>
        </div>
      )}

      {!showingDismissed && hasAnyResults && noVisibleTests && (
        <p className="muted-text">
          No {resultFilter === RESULT_FILTER_PASS ? 'passing' : 'failing'} tests.
        </p>
      )}

      {/* Add test case buttons */}
      {!showingDismissed && (
        <div className="test-cases-add">
          <AddTestCaseForm
            onAddTestCase={addManualTestCase}
            criteriaScid={criteriaScid}
            organizationId={organizationId}
            existingCallIds={existingCallIds}
          />
        </div>
      )}
    </div>
  )
}

/**
 * Component to display test section header with run all button
 */
export const TestSectionHeader = ({
  prompt,
  allEvidence,
  allTestResults,
  runAllLoading,
  handleRunAllTests,
  manualTestCases = [],
  expandedTests = {},
  setExpandedTests,
}) => {
  const promptTests = prompt?.prompt_tests || []
  const totalTestCount = allEvidence.length + manualTestCases.length + promptTests.length

  if (!prompt || totalTestCount === 0) {
    return null
  }

  const allTestIds = [
    ...allEvidence.map((evidence) => evidence.id),
    ...manualTestCases.map((testCase) => testCase.id),
    ...promptTests.map((test) => `prompt-test-${test.id}`),
  ]
  const anyExpanded = allTestIds.some((testId) => expandedTests[testId] !== false)

  const handleToggleAll = () => {
    if (anyExpanded) {
      setExpandedTests(allTestIds.reduce((acc, testId) => ({ ...acc, [testId]: false }), {}))
    } else {
      setExpandedTests({})
    }
  }

  return (
    <div className="flex-align-center small-gap">
      {!isEmpty(allTestResults) && (
        <span className="muted-text small">
          {Object.values(allTestResults).filter((result) => result.responses_match).length}/
          {Object.keys(allTestResults).length} passed
        </span>
      )}
      {totalTestCount > 1 && setExpandedTests && (
        <Button small basic onClick={handleToggleAll}>
          {anyExpanded ? 'Collapse all' : 'Expand all'}
        </Button>
      )}
      <Button
        small
        onClick={handleRunAllTests}
        loading={runAllLoading}
        disabled={runAllLoading}
        data-testid="run-all-tests-button"
      >
        {totalTestCount > 1 ? 'Run All Tests' : 'Run Test'}
      </Button>
    </div>
  )
}

/**
 * Component to display criteria context
 */
export const CriteriaContext = ({ matchingCriteria, mustBePresent }) => {
  if (!matchingCriteria) {
    return null
  }

  const passResponse = mustBePresent ? 'yes' : 'no'
  const failResponse = mustBePresent ? 'no' : 'yes'

  return (
    <div className="criteria-context">
      Tests{' '}
      <Pill xs success>
        Pass
      </Pill>{' '}
      when AI responds &quot;{passResponse}&quot;,
      <Pill xs critical>
        Fail
      </Pill>{' '}
      when AI responds &quot;{failResponse}&quot;
    </div>
  )
}
